import React, { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { MapPin, BedDouble, Bath, Square, CheckCircle, ArrowRight, ChevronLeft } from 'lucide-react'
import { projects } from '../data/projects'

import SEO from './SEO'

const PropertyDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const project = projects.find(p => p.slug === id);

    const [formData, setFormData] = useState({ name: '', phone: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Script URL
    const scriptUrl = 'https://script.google.com/macros/s/AKfycbxykQSU-HiL3vN4oEEXn8Satcyv96AGar6J6-PIb387PNkgFNHWkonQlT5wOnp596m6/exec';

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const response = await fetch(scriptUrl, {
                redirect: "follow",
                method: 'POST',
                headers: {
                    "Content-Type": "text/plain;charset=utf-8",
                },
                body: JSON.stringify({
                    ...formData,
                    formType: 'contact', // Force 'contact' as GAS only accepts this
                    projectName: project?.title || 'Unknown Project' // Attempt to send project name even if ignored by current script
                }),
            });

            const result = await response.json();

            if (result.success) {
                alert("Enquiry sent successfully!");
                setFormData({ name: '', phone: '' });
            } else {
                alert("Something went wrong. Please try again.");
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            alert("Error sending enquiry. Please try again later.");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!project) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] pt-20">
                <SEO title="Project Not Found" description="The requested project could not be found." />
                <h2 className="text-3xl font-bold text-gray-800 mb-4">Project Not Found</h2>
                <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium flex items-center gap-2">
                    <ChevronLeft className="w-5 h-5" /> Back to Home
                </Link>
            </div>
        );
    }

    return (
        <div className="pt-0"> {/* Removed pt-20 because hero is huge */}
            <SEO
                title={`${project.title} in ${project.location} | Price, Reviews & Floor Plan`}
                description={`Buy ${project.title} in ${project.location}. ${project.specs?.bedrooms || 'Luxury'} homes starting at ${project.price}. Verified listings, high returns, and premium amenities.`}
                image={project.image}
                keywords={`${project.title}, ${project.location} property, buy flat in ${project.location}, real estate ${project.location}`}
            />

            {/* Property Hero */}
            <div className="relative h-[60vh] md:h-[70vh] bg-gray-900">
                <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover opacity-60"
                />
                <div className="absolute top-24 left-4 md:left-8 z-10">
                    <Link to="/" className="text-white/80 hover:text-white flex items-center gap-2 bg-black/30 backdrop-blur-sm px-4 py-2 rounded-full transition-colors">
                        <ChevronLeft className="w-5 h-5" /> Back
                    </Link>
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-8 pb-16 bg-gradient-to-t from-black/90 via-black/50 to-transparent">
                    <div className="max-w-7xl mx-auto text-white">
                        <div className="flex flex-col md:flex-row justify-between items-end gap-6">
                            <div>
                                <span className={`px-3 py-1 text-xs font-bold uppercase rounded-sm mb-2 inline-block ${project.status === 'Ready to Move' ? 'bg-green-600' : 'bg-primary-500'}`}>
                                    {project.status}
                                </span>
                                <h1 className="text-4xl md:text-6xl font-bold font-bricolage mb-2">{project.title}</h1>
                                <p className="flex items-center text-lg md:text-xl text-gray-300">
                                    <MapPin className="w-5 h-5 mr-2 text-primary-500" />
                                    {project.location}
                                </p>
                            </div>
                            <div className="text-right md:text-right text-left">
                                <p className="text-sm text-gray-400 uppercase tracking-widest">Price</p>
                                <p className="text-3xl md:text-5xl font-bold text-primary-400 whitespace-nowrap">{project.price}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">

                    {/* Left Column - Details */}
                    <div className="lg:col-span-2 space-y-12">

                        {/* Key Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-6 border-b border-gray-200 pb-12">
                            {project.specs?.bedrooms && (
                                <div className="flex items-center gap-3">
                                    <div className="p-3 bg-primary-50 rounded-full text-primary-600">
                                        <BedDouble className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Bedrooms/Type</p>
                                        <p className="font-bold text-gray-900">{project.specs.bedrooms}</p>
                                    </div>
                                </div>
                            )}

                            {project.specs?.area && (
                                <div className="flex items-center gap-3">
                                    <div className="p-3 bg-primary-50 rounded-full text-primary-600">
                                        <Square className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Area</p>
                                        <p className="font-bold text-gray-900">{project.specs.area}</p>
                                    </div>
                                </div>
                            )}

                            {/* Fallback for third stat if bath exists or just status again */}
                            {project.specs?.bathrooms ? (
                                <div className="flex items-center gap-3">
                                    <div className="p-3 bg-primary-50 rounded-full text-primary-600">
                                        <Bath className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Bathrooms</p>
                                        <p className="font-bold text-gray-900">{project.specs.bathrooms}</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-center gap-3">
                                    <div className="p-3 bg-primary-50 rounded-full text-primary-600">
                                        <CheckCircle className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Status</p>
                                        <p className="font-bold text-gray-900">{project.status}</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Description */}
                        <div>
                            <h2 className="text-2xl font-bold mb-6 text-gray-900">About Project</h2>
                            <div className="prose prose-lg text-gray-600 leading-relaxed">
                                <p>{project.description}</p>
                            </div>
                        </div>

                        {/* Amenities */}
                        {project.amenities && (
                            <div>
                                <h2 className="text-2xl font-bold mb-6 text-gray-900">Premium Amenities</h2>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-y-4 gap-x-8">
                                    {project.amenities.map(item => (
                                        <div key={item} className="flex items-center gap-2 text-gray-700">
                                            <CheckCircle className="w-5 h-5 text-primary-500 flex-shrink-0" />
                                            <span>{item}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                    </div>

                    {/* Right Column - Form */}
                    <div className="lg:col-span-1">
                        <div className="bg-white p-6 rounded-2xl shadow-xl border border-gray-100 sticky top-24">
                            <h3 className="text-2xl font-bold mb-2">Interested?</h3>
                            <p className="text-gray-500 mb-6">Fill out the form to schedule a site visit.</p>

                            <form className="space-y-4" onSubmit={handleSubmit}>
                                <input type="hidden" name="project_name" value={project.title} />
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="Name"
                                    required
                                    className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none transition-all"
                                />
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    placeholder="Phone"
                                    required
                                    className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none transition-all"
                                />
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full bg-primary-500 text-white font-bold py-3 rounded-lg hover:bg-primary-600 transition-colors flex items-center justify-center gap-2 shadow-lg shadow-primary-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isSubmitting ? 'Sending...' : 'Request Call Back'}
                                    {!isSubmitting && <ArrowRight className="w-4 h-4" />}
                                </button>
                            </form>

                            <div className="mt-6 pt-6 border-t border-gray-100 text-center">
                                <p className="text-sm text-gray-500 mb-2">Or call us directly at</p>
                                <a href="tel:+918368225412" className="text-xl font-bold text-gray-900 hover:text-primary-500 transition-colors">
                                    +91-83682-25412
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    )
}

export default PropertyDetail
